import React, { useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { __ } from 'locale';
import {
    openModal, closeModal,
    addFoodGroup,
    changeFoodGroupType as changeFoodGroupTypeAction,
    setFoodGroupTitle,
    moveFoodGroup as moveFoodGroupAction,
    removeFoodGroup,
    modifyFoodGroupAmounts,
} from '../../../actions/index';
import FoodItem from './FoodItem';
import LectureTaskMealDescription from './LectureTaskMealDescription';
import LectureTaskPanelAddFood from './LectureTaskPanelAddFood';
import LectureTaskPanelAddRecipe from './LectureTaskPanelAddRecipe';
import DropdownMenu from 'components/shared/DropdownMenu';
import ConfirmDialog from 'components/shared/ConfirmDialog';
import Util from 'utility';

const headerBtnCls = 'no-underline inline-flex py-[6px] px-[8px] rounded-full bg-[#EBEBEB] text-black';

function FoodGroup({ foodGroup: fg, mealHash: m, activePanel, togglePanel, closePanel, foodGroupVisibility, onToggleVisibility, focusedAmountInputId, focusedAmountInputValue, onAmountFocus, onAmountChange, onAmountBlur, onSave, onSaveAndUpdate }) {
    const dispatch = useDispatch();
    const globals = useSelector(state => state.globals);
    const foods = useSelector(state => state.foods[m]);
    const foodGroupAmountRef = useRef(100);

    const changeFoodGroupType = (type) => {
        dispatch(changeFoodGroupTypeAction({ mealHash: m, id: fg.id, type }));
        closePanel();
        onSaveAndUpdate();
    };

    const handleChangeFoodGroupTitle = (e) => {
        dispatch(setFoodGroupTitle({ mealHash: m, id: fg.id, title: e.target.value }));
    };

    const handleMoveFoodGroup = (dir) => {
        closePanel();
        dispatch(moveFoodGroupAction({ mealHash: m, id: fg.id, direction: dir }));
    };

    const doDeleteFoodGroup = () => {
        dispatch(removeFoodGroup({ mealHash: m, id: fg.id }));
        onSaveAndUpdate();
    };

    const handleDeleteFoodGroup = () => {
        dispatch(openModal({
            title: __('Smazat talíř'),
            content: <ConfirmDialog message={__('Opravdu smazat talíř?')} confirmLabel={__('Ano, smazat')} onConfirm={doDeleteFoodGroup} />
        }));
    };

    const openEditDescription = (e) => {
        e.preventDefault();
        if (e.target !== e.currentTarget) return;
        closePanel();
        dispatch(closeModal());
        dispatch(openModal({
            title: __('Postup přípravy receptu'),
            class: 'dialog-flexible',
            closeButton: __('Uložit a zavřít'),
            onClose: onSave,
            content: <LectureTaskMealDescription mealHash={m} foodGroupId={fg.id} />
        }));
    };

    const openAddFood = (e) => {
        if (e) { e.preventDefault(); if (e.target !== e.currentTarget) return; }
        dispatch(closeModal());
        dispatch(openModal({ title: __('Přidat potravinu'), class: 'dialog-big', content: <LectureTaskPanelAddFood mealId={m} groupId={fg.id} /> }));
    };

    const openAddRecipe = (e) => {
        if (e) { e.preventDefault(); if (e.target !== e.currentTarget) return; }
        dispatch(closeModal());
        dispatch(openModal({ title: __('Přidat recept'), class: 'dialog-big', content: <LectureTaskPanelAddRecipe mealId={m} onRecipeAdded={onSaveAndUpdate} /> }));
    };

    const openChangeFoodGroupAmount = () => {

        let txt = "Zadejte procento původního množství (např. 150 = +50 %)";
        if(Util.getLang() == 'sk') {
            txt = "Zadajte percento pôvodného množstva (napr. 150 = +50 %)";
        }

        dispatch(closeModal());
        dispatch(openModal({
            title: __('Změnit množství potravin v chodu'),
            content: (
                <div className="p-[0.5em]">
                    <p className="mb-[0.75em] text-[0.9em] text-[#555]">{txt}:</p>
                    <div className="flex items-center gap-[0.5em] mb-[1em]">
                        <input className="w-[5em] p-[0.4em] text-center border border-[#ccc] rounded" type="text" defaultValue={foodGroupAmountRef.current} onChange={e => { foodGroupAmountRef.current = e.target.value; }} />
                        <span className="text-[#555]">%</span>
                    </div>
                    <div className="flex items-center gap-[1.5em]">
                        <button className="bg-[#26AA52] text-white py-[0.5em] px-[1em] rounded cursor-pointer border-none hover:bg-[#2dcb62]" onClick={doUpdateFoodGroupAmount}>{__('Použít')}</button>
                        <a className="text-[#999] underline cursor-pointer" onClick={() => dispatch(closeModal())}>{__('Zpět')}</a>
                    </div>
                </div>
            )
        }));
    };

    const doUpdateFoodGroupAmount = () => {
        dispatch(modifyFoodGroupAmounts({ mealHash: m, group_id: fg.id, coefficient: foodGroupAmountRef.current * 1 }));
        dispatch(closeModal());
        onSaveAndUpdate();
    };

    const fgVisible = foodGroupVisibility[fg.id] !== 0;


    const iconDown = <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="size-4"><path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>;
    const iconUp = <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="size-4"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" /></svg>;

    const fgVisIcon = fgVisible ? iconUp : iconDown;

    const sortedFoods = foods.filter(f => f.group_id === fg.id);
    const totalFoodWeight = sortedFoods.reduce((sum, fd) => sum + fd.weight * 1, 0);

    const settingsPanel = activePanel.type === 'groupSettings' && activePanel.key === fg.id
        ? (
            <DropdownMenu
                top="2.75em"
                items={[
                    { label: __('Přesunout výše'), onClick: () => handleMoveFoodGroup(-1) },
                    { label: __('Přesunout níže'), onClick: () => handleMoveFoodGroup(1) },
                    { label: __('Popis přípravy'), onClick: openEditDescription },
                    { label: __('Smazat talíř'), onClick: handleDeleteFoodGroup, danger: true },
                ]}
            />
        ) : false;

    const addFoodOrRecipePanel = activePanel.type === 'addFood' && activePanel.key === fg.id
        ? (
            <DropdownMenu
                top="2.75em"
                items={[
                    { label: __('Přidat potravinu'), onClick: openAddFood },
                    { label: __('Přidat recept'), onClick: openAddRecipe },
                ]}
            />
        ) : false;

    const groupTypePanel = activePanel.type === 'groupType' && activePanel.key === fg.id
        ? (
            <DropdownMenu
                top="1.5em"
                align="left"
                items={Object.keys(globals.foodGroupTypes).map(type => ({
                    label: globals.foodGroupTypes[type],
                    onClick: () => changeFoodGroupType(type),
                }))}
            />
        ) : false;

    if (!fgVisible) {
        return (
            <div className="my-[1em] mx-[1.5em]">
                <div className="flex items-center rounded border border-[#d8d8d8] py-[0.5em] pr-[0.5em] pl-[1.5em] text-[1.05em] text-[#404040] bg-white">
                    <p className="m-0 text-[#737373]">
                        {globals.foodGroupTypes[fg.type]}:
                        <b className="text-[#404040] text-[1.1em] ml-[0.35rem]">{fg.title}</b>
                    </p>
                    <aside className="ml-auto ml-[3em]">
                        <a className="flex items-center justify-center w-[1.6em] h-[1.6em] bg-[#8d95a4] text-white rounded-full" onClick={() => onToggleVisibility(fg.id)}>{fgVisIcon}</a>
                    </aside>
                </div>
            </div>
        );
    }

    return (
        <div className="mb-[1px] relative">
            <div className="flex items-center p-[1.5em] bg-[#F7F8FA] max-[719px]:flex-col max-[719px]:p-[1em] max-[719px]:items-start">
                <div className="m-0 flex-1 relative">
                    <a className="text-[#777] uppercase text-[0.9em]" onClick={() => togglePanel('groupType', fg.id)}>
                        {globals.foodGroupTypes[fg.type]} <i className="fas fa-angle-down"></i>
                    </a>
                    <input
                        className="block font-bold bg-transparent pt-[6px] pb-[10px] pr-0 pl-0 w-[calc(100%-5em)] border-0 text-[1.35em] text-[#404040] focus:outline-none"
                        type="text" placeholder={__('Název')}
                        value={fg.title}
                        onChange={handleChangeFoodGroupTitle}
                        onBlur={onSave}
                    />
                    {groupTypePanel}
                </div>
                <aside className="ml-auto text-[0.9em] flex items-center gap-[0.75em] max-[719px]:ml-0">
                    <div className="relative">
                        <a className={`click-outside-disabled ${headerBtnCls} !bg-[#26AA52] !text-white hover:!bg-[#2bbf5c]`} onClick={() => togglePanel('addFood', fg.id)}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="size-4">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                        </a>
                        {addFoodOrRecipePanel}
                    </div>
                    <div className="relative">
                        <a className={headerBtnCls} onClick={() => onToggleVisibility(fg.id)}>{fgVisIcon}</a>
                    </div>
                    <div className="relative">
                        <a className={headerBtnCls} onClick={openChangeFoodGroupAmount}>
                            <i className="fa fa-balance-scale"></i>
                            <span className="ml-[8px]">{Math.round(totalFoodWeight * 10) / 10} g/ml</span>
                        </a>
                    </div>
                    <div className="relative">
                        <a className={`click-outside-disabled ${headerBtnCls}`} onClick={() => togglePanel('groupSettings', fg.id)}>
                            <i className="fa fa-pencil-alt"></i> <span className="ml-[8px]">Možnosti</span>
                        </a>
                        {settingsPanel}
                    </div>
                </aside>
            </div>
            <div className="flex flex-col">
                {sortedFoods.map(f => (
                    <FoodItem
                        key={f.id}
                        food={f}
                        mealHash={m}
                        focusedAmountInputId={focusedAmountInputId}
                        focusedAmountInputValue={focusedAmountInputValue}
                        onAmountFocus={onAmountFocus}
                        onAmountChange={onAmountChange}
                        onAmountBlur={onAmountBlur}
                        onSave={onSave}
                        onSaveAndUpdate={onSaveAndUpdate}
                    />
                ))}
            </div>
        </div>
    );
}

export default FoodGroup;
